import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, Loader2, Eye, TrendingUp, Trophy, Rocket, Users, Star, Crown } from 'lucide-react';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';

const FeaturedMarket = () => {
  const [markets, setMarkets] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedMarkets = async () => {
      try {
        const data = await apiService.getMarkets('OPEN', null, null, 5);
        setMarkets(data.markets || []);
      } catch (err) {
        console.error('Failed to fetch featured markets:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedMarkets();
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % markets.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + markets.length) % markets.length);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400 text-sm">Loading featured markets...</span>
        </div>
      </div>
    );
  }

  if (markets.length === 0) {
    return null;
  }

  const currentMarket = markets[currentSlide];
  const yesPrice = currentMarket.prices?.YES_price_bp ? Math.round(currentMarket.prices.YES_price_bp / 100) : 50;
  const noPrice = 100 - yesPrice;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left Side - Compact Featured Market Card */}
        <div className="lg:col-span-2 bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
          {/* Market Header */}
          <div className="p-4">
            <div className="flex items-start gap-3">
              <img
                src={currentMarket.image || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=200&h=200&fit=crop'}
                alt={currentMarket.title}
                className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=200&h=200&fit=crop';
                }}
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-white line-clamp-2 leading-tight">
                  {currentMarket.title}
                </h3>
                {currentMarket.category && (
                  <span className="inline-block mt-1.5 px-2 py-0.5 text-xs font-medium text-[#F97316] bg-[#F97316]/10 rounded-full">
                    {currentMarket.category}
                  </span>
                )}
              </div>
            </div>

            {/* Compact Price Bar */}
            <div className="mt-4 relative h-6 bg-[#1e2a45] rounded-full overflow-hidden">
              <div 
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#3B82F6] to-[#60A5FA]"
                style={{ width: `${yesPrice}%` }}
              />
              <div 
                className="absolute right-0 top-0 h-full bg-gradient-to-l from-[#F97316] to-[#FBBF24]"
                style={{ width: `${noPrice}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold text-white drop-shadow-lg">
                  {yesPrice}% YES / {noPrice}% NO
                </span>
              </div>
            </div>

            {/* Options Buttons - Compact */}
            <div className="flex gap-2 mt-3">
              <button
                data-testid="featured-yes-btn"
                className="flex-1 py-2 px-3 rounded-lg text-sm font-semibold text-white bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] shadow-[0_3px_0_0_#1E40AF] hover:shadow-[0_1px_0_0_#1E40AF] hover:translate-y-[2px] active:translate-y-[3px] active:shadow-none transition-all"
              >
                Yes {yesPrice}TK¢
              </button>
              <button
                data-testid="featured-no-btn"
                className="flex-1 py-2 px-3 rounded-lg text-sm font-semibold text-white bg-gradient-to-b from-[#FB923C] to-[#EA580C] shadow-[0_3px_0_0_#C2410C] hover:shadow-[0_1px_0_0_#C2410C] hover:translate-y-[2px] active:translate-y-[3px] active:shadow-none transition-all"
              >
                No {noPrice}TK¢
              </button>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1e2a45]">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="font-medium text-white">{currentMarket.status}</span>
                {currentMarket.closes_at && (
                  <>
                    <RefreshCw className="h-3 w-3" />
                    <span>Closes {new Date(currentMarket.closes_at).toLocaleDateString()}</span>
                  </>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={prevSlide}
                className="p-1 rounded-full border border-[#1e2a45] hover:bg-[#1e2a45] transition-colors"
              >
                <ChevronLeft className="h-4 w-4 text-gray-400" />
              </button>
              <div className="flex items-center gap-1.5">
                {markets.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`h-1.5 rounded-full transition-all ${
                      currentSlide === idx ? 'bg-[#3B82F6] w-4' : 'bg-gray-600 w-1.5 hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={nextSlide}
                className="p-1 rounded-full border border-[#1e2a45] hover:bg-[#1e2a45] transition-colors"
              >
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Right Side - Promotional Content */}
        <div className="lg:col-span-3 bg-gradient-to-br from-[#131b2e] to-[#0a1020] rounded-2xl border border-[#1e2a45] overflow-hidden">
          <div className="p-5 h-full flex flex-col">
            {/* Hero Section */}
            <div className="mb-4">
              <h2 className="text-xl font-bold text-white mb-2">
                See the future before it happens.
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                Curious about how events might unfold? <span className="text-[#3B82F6] font-medium">AMPPredict.com</span> offers 
                insights based on probability for informational and educational purposes. Combining elements of Kalshi and 
                Polymarket, AMPPredict uses user-led ideas to mock predict future events without spending real money.
              </p>
            </div>

            {/* What do I do next? */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-[#3B82F6] mb-2 flex items-center gap-2">
                <Rocket className="h-4 w-4" />
                What do I do next?
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 text-xs text-gray-300 bg-[#1e2a45]/50 rounded-lg px-3 py-2">
                  <Eye className="h-3.5 w-3.5 text-[#3B82F6]" />
                  Explore live markets
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-300 bg-[#1e2a45]/50 rounded-lg px-3 py-2">
                  <TrendingUp className="h-3.5 w-3.5 text-[#F97316]" />
                  Place a mock prediction
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-300 bg-[#1e2a45]/50 rounded-lg px-3 py-2">
                  <Star className="h-3.5 w-3.5 text-yellow-400" />
                  Track your accuracy
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-300 bg-[#1e2a45]/50 rounded-lg px-3 py-2">
                  <Trophy className="h-3.5 w-3.5 text-green-400" />
                  Climb the leaderboard
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 italic">
                Start predicting the future of your events—risk free.
              </p>
            </div>

            {/* User Tiers */}
            <div className="grid grid-cols-3 gap-2 mt-auto">
              {/* Base User */}
              <div className="bg-[#1e2a45]/30 rounded-xl p-3 border border-[#1e2a45] hover:border-gray-500 transition-colors">
                <div className="flex items-center gap-1.5 mb-2">
                  <Users className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-300">Base User</span>
                </div>
                <ul className="space-y-1">
                  <li className="text-xs text-gray-500 flex items-start gap-1">
                    <span className="text-gray-600 mt-0.5">•</span>
                    Browse all Markets free
                  </li>
                  <li className="text-xs text-gray-500 flex items-start gap-1">
                    <span className="text-gray-600 mt-0.5">•</span>
                    Open Trading Portal
                  </li>
                  <li className="text-xs text-gray-500 flex items-start gap-1">
                    <span className="text-gray-600 mt-0.5">•</span>
                    View Leaderboard
                  </li>
                  <li className="text-xs text-gray-500 flex items-start gap-1">
                    <span className="text-gray-600 mt-0.5">•</span>
                    See Final Scores
                  </li>
                </ul>
              </div>

              {/* Intermediate User */}
              <div className="bg-[#3B82F6]/10 rounded-xl p-3 border border-[#3B82F6]/30 hover:border-[#3B82F6]/50 transition-colors">
                <div className="flex items-center gap-1.5 mb-2">
                  <Star className="h-3.5 w-3.5 text-[#3B82F6]" />
                  <span className="text-xs font-semibold text-[#3B82F6]">Logged In</span>
                </div>
                <ul className="space-y-1">
                  <li className="text-xs text-gray-400 flex items-start gap-1">
                    <span className="text-[#3B82F6] mt-0.5">•</span>
                    Submit & Edit Markets
                  </li>
                  <li className="text-xs text-gray-400 flex items-start gap-1">
                    <span className="text-[#3B82F6] mt-0.5">•</span>
                    Share Invite Free
                  </li>
                  <li className="text-xs text-gray-400 flex items-start gap-1">
                    <span className="text-[#3B82F6] mt-0.5">•</span>
                    Filter by Category
                  </li>
                  <li className="text-xs text-gray-400 flex items-start gap-1">
                    <span className="text-[#3B82F6] mt-0.5">•</span>
                    Like/Unlike Markets
                  </li>
                </ul>
              </div>

              {/* Full User */}
              <div className="bg-gradient-to-br from-[#F97316]/10 to-[#F97316]/5 rounded-xl p-3 border border-[#F97316]/30 hover:border-[#F97316]/50 transition-colors relative overflow-hidden">
                <div className="absolute top-0 right-0 px-1.5 py-0.5 bg-[#F97316] text-white text-[10px] font-bold rounded-bl-lg">
                  PRO
                </div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Crown className="h-3.5 w-3.5 text-[#F97316]" />
                  <span className="text-xs font-semibold text-[#F97316]">Subscriber</span>
                </div>
                <ul className="space-y-1">
                  <li className="text-xs text-gray-400 flex items-start gap-1">
                    <span className="text-[#F97316] mt-0.5">•</span>
                    Full Participation
                  </li>
                  <li className="text-xs text-gray-400 flex items-start gap-1">
                    <span className="text-[#F97316] mt-0.5">•</span>
                    Buy & Sell Binary
                  </li>
                  <li className="text-xs text-gray-400 flex items-start gap-1">
                    <span className="text-[#F97316] mt-0.5">•</span>
                    Triple Predictions
                  </li>
                  <li className="text-xs text-gray-400 flex items-start gap-1">
                    <span className="text-[#F97316] mt-0.5">•</span>
                    Invite to Like Markets
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedMarket;
;
